import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { gql, useApolloClient } from "@apollo/client";

export type User = {
  id: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
  sex?: string | null;
  suburb?: string | null;
  postalCode?: string | null;
  dob?: string | null;
  termsVersion?: string | null;
};

type AuthContextValue = {
  user: User | null;
  isLoggedIn: boolean;
  loginWithOneClickCode: (code: string) => Promise<User | null>;
  loginWithCode: (email: string, code: string) => Promise<User | null>;
  loginWithGoogleJwt: (
    googleJwt: string,
    opts?: { conditionId: number; countryCode?: string; locale?: string }
  ) => Promise<User | null>;
  registerWithEmail: (
    params: { conditionId: number; email: string; countryCode?: string; locale?: string }
  ) => Promise<User | null>;
  sendLoginCode: (
    email: string,
    preferredLoginCodeFlowType?: 'EMAIL' | 'SMS'
  ) => Promise<{ loginCodeDestination?: string | null; loginCodeFlowType?: 'EMAIL' | 'SMS' | null } | null>;
  logout: () => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const LOGIN_PATIENT = gql`
  mutation LoginPatient($input: UserCredentialsInput!) {
    loginPatient(input: $input) {
      id
      email
      firstName
      lastName
      phoneNumber
      sex
      suburb
      postalCode
      dob
      termsVersion
    }
  }
`;

const REGISTER_PATIENT = gql`
  mutation RegisterPatient($input: RegisterRequestInput!) {
    registerPatient(input: $input) {
      id
      email
      firstName
      lastName
      phoneNumber
      sex
      suburb
      postalCode
      dob
      termsVersion
    }
  }
`;

const SEND_LOGIN_CODE_NEW = gql`
  mutation SendLoginCodeNew($input: String!, $preferredLoginCodeFlowType: LoginCodeFlowType!) {
    sendLoginCodeNew(input: $input, preferredLoginCodeFlowType: $preferredLoginCodeFlowType) {
      loginCodeDestination
      loginCodeFlowType
    }
  }
`;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const apollo = useApolloClient();
  const [user, setUser] = useState<User | null>(null);

  const isLoggedIn = !!user;

  const loginWithOneClickCode = useCallback(async (code: string) => {
    try {
      const res = await apollo.mutate({
        mutation: LOGIN_PATIENT,
        variables: { input: { oneClickLogin: { oneClickLoginCode: code } } },
      });
      const patient = (res.data as any)?.loginPatient;
      if (patient?.id) {
        const newUser = {
          id: patient.id,
          email: patient.email ?? null,
          firstName: patient.firstName ?? null,
          lastName: patient.lastName ?? null,
          phoneNumber: patient.phoneNumber ?? null,
          sex: patient.sex ?? null,
          suburb: patient.suburb ?? null,
          postalCode: patient.postalCode ?? null,
          dob: patient.dob ?? null,
          termsVersion: patient.termsVersion ?? null,
        };
        setUser(newUser);
        return newUser;
      } else {
        // If no patient data, throw an error to indicate login failure
        throw new Error("Invalid or expired login code");
      }
    } catch (error) {
      // Re-throw the error so the calling code can handle it
      throw error;
    }
  }, [apollo]);

  const loginWithCode = useCallback(
    async (email: string, code: string) => {
      try {
        const res = await apollo.mutate({
          mutation: LOGIN_PATIENT,
          variables: { input: { email, loginCode: code } },
        });
        const patient = (res.data as any)?.loginPatient;
        if (patient?.id) {
          const u: User = {
            id: patient.id,
            email: patient.email ?? null,
            firstName: patient.firstName ?? null,
            lastName: patient.lastName ?? null,
            phoneNumber: patient.phoneNumber ?? null,
            sex: patient.sex ?? null,
            suburb: patient.suburb ?? null,
            postalCode: patient.postalCode ?? null,
            dob: patient.dob ?? null,
            termsVersion: patient.termsVersion ?? null,
          };
          setUser(u);
          return u;
        }
        // If no patient data, throw an error to indicate login failure
        throw new Error("Invalid or expired login code");
      } catch (error) {
        // Re-throw the error so the calling code can handle it
        throw error;
      }
    },
    [apollo],
  );

  const loginWithGoogleJwt = useCallback(async (
    googleJwt: string,
    opts?: { conditionId: number; countryCode?: string; locale?: string },
  ) => {
    try {
      const res = await apollo.mutate({
        mutation: LOGIN_PATIENT,
        variables: { input: { googleJwtToken: googleJwt } },
      });
      const patient = (res.data as any)?.loginPatient;
      if (patient?.id) {
        const u: User = {
          id: patient.id,
          email: patient.email ?? null,
          firstName: patient.firstName ?? null,
          lastName: patient.lastName ?? null,
          phoneNumber: patient.phoneNumber ?? null,
          sex: patient.sex ?? null,
          suburb: patient.suburb ?? null,
          postalCode: patient.postalCode ?? null,
          dob: patient.dob ?? null,
          termsVersion: patient.termsVersion ?? null,
        };
        setUser(u);
        return u;
      }
      return null;
    } catch (e: any) {
      const graphQLErrors = e?.graphQLErrors || [];
      const isUnauthorized = graphQLErrors.some((err: any) =>
        err?.extensions?.apiErrorCode === "AUTH_MISSING" || err?.message === "Unauthorized",
      );
      if (!isUnauthorized) throw e;
      // Fallback: register new patient using Google JWT, then treat as logged in
      // Decode Google JWT to prefill registration
      let email: string | undefined;
      let firstName: string | undefined;
      let lastName: string | undefined;
      try {
        const base64Url = googleJwt.split(".")[1];
        const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
        const jsonPayload = decodeURIComponent(
          atob(base64)
            .split("")
            .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
            .join("")
        );
        const payload = JSON.parse(jsonPayload);
        email = payload.email;
        firstName = payload.given_name || payload.name?.split(" ")[0];
        lastName = payload.family_name || payload.name?.split(" ").slice(1).join(" ");
      } catch {}

      const reg = await apollo.mutate({
        mutation: REGISTER_PATIENT,
        variables: {
          input: {
            conditionID: opts?.conditionId,
            country: opts?.countryCode as any,
            locale: (opts?.locale as any) || undefined,
            email,
            firstName,
            lastName,
          },
        },
      });
      const patient = (reg.data as any)?.registerPatient;
      if (patient?.id) {
        const u: User = {
          id: patient.id,
          email: patient.email ?? null,
          firstName: patient.firstName ?? null,
          lastName: patient.lastName ?? null,
          phoneNumber: patient.phoneNumber ?? null,
          sex: patient.sex ?? null,
          suburb: patient.suburb ?? null,
          postalCode: patient.postalCode ?? null,
          dob: patient.dob ?? null,
          termsVersion: patient.termsVersion ?? null,
        };
        setUser(u);
        return u;
      }
      return null;
    }
  }, [apollo]);

  const registerWithEmail = useCallback(
    async (params: { conditionId: number; email: string; countryCode?: string; locale?: string }) => {
      const reg = await apollo.mutate({
        mutation: REGISTER_PATIENT,
        variables: {
          input: {
            conditionID: params.conditionId,
            email: params.email,
            country: params.countryCode as any,
            locale: (params.locale as any) || undefined,
            emailValidation: true,
          },
        },
      });
      const patient = (reg.data as any)?.registerPatient;
      if (patient?.id) {
        const u: User = {
          id: patient.id,
          email: patient.email ?? null,
          firstName: patient.firstName ?? null,
          lastName: patient.lastName ?? null,
          phoneNumber: patient.phoneNumber ?? null,
          sex: patient.sex ?? null,
          suburb: patient.suburb ?? null,
          postalCode: patient.postalCode ?? null,
          dob: patient.dob ?? null,
          termsVersion: patient.termsVersion ?? null,
        };
        setUser(u);
        return u;
      }
      return null;
    },
    [apollo],
  );

  const sendLoginCode = useCallback(
    async (
      email: string,
      preferredLoginCodeFlowType: 'EMAIL' | 'SMS' = 'EMAIL',
    ): Promise<{
      loginCodeDestination?: string | null;
      loginCodeFlowType?: 'EMAIL' | 'SMS' | null;
    } | null> => {
      const res = await apollo.mutate({
        mutation: SEND_LOGIN_CODE_NEW,
        variables: {
          input: email,
          preferredLoginCodeFlowType,
        },
      });
      const data = (res.data as any)?.sendLoginCodeNew;
      if (!data) return null;
      return {
        loginCodeDestination: data.loginCodeDestination ?? null,
        loginCodeFlowType: (data.loginCodeFlowType as 'EMAIL' | 'SMS') ?? null,
      };
    },
    [apollo],
  );

  const logout = useCallback(() => {
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      isLoggedIn,
      loginWithOneClickCode,
      loginWithCode,
      loginWithGoogleJwt,
      registerWithEmail,
      sendLoginCode,
      logout,
    }),
    [
      user,
      isLoggedIn,
      loginWithOneClickCode,
      loginWithCode,
      loginWithGoogleJwt,
      registerWithEmail,
      sendLoginCode,
      logout,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}


